/**
 * Every outbound destination on the landing page, in one place.
 *
 * The landing page is static and carries no auth code — its only tie to the
 * product app is `SIGN_IN_URL` below, a plain top-level navigation. See
 * docs/sso-cross-domain-login.md and ADR 0009.
 */

/** Product app origin. The landing page never fetches from it, only links to it. */
const APP_ORIGIN = 'https://app.orbiswisdomx.com';

/**
 * Single sign-in entry point, shared by the nav button and every in-page CTA.
 *
 * CRITICAL: this points at the SSO service's /login, NOT at the app's /login
 * page. Linking to `${APP_ORIGIN}/login` would render the app's sign-in screen
 * and force the user to click "Sign in with Microsoft" a SECOND time — the
 * double-login the client explicitly rejected.
 *
 * `next` is honoured by the SSO service (signed into the OAuth `state`), so the
 * user lands on /home already authenticated after one Entra prompt.
 */
export const SIGN_IN_URL = `${APP_ORIGIN}/sso-api/login?next=${encodeURIComponent(
  `${APP_ORIGIN}/home`,
)}`;

/**
 * TODO(wisdomx): replace with the real sales address before the public launch.
 * Deliberately a single constant so this is a one-line change.
 */
const DEMO_EMAIL = 'hello@orbiswisdomx.com';

/**
 * Sales address as printed in the client's own whitepaper (Oct 2026). Used by
 * the blog's pilot-partner CTA. NOTE: differs from the DEMO_EMAIL placeholder
 * above — confirm with the client whether the demo CTAs should use this too.
 */
export const SALES_EMAIL = 'sales@orbiswisdomx.com';

/** Pilot-partner / consultation funnel (HubSpot lead capture). */
export const QUALIFIER_PATH = '/qualifier';

/** "Request a Demo" / "Request Platform Demo" — opens the visitor's mail client. */
export const DEMO_MAILTO_URL =
  `mailto:${DEMO_EMAIL}` +
  `?subject=${encodeURIComponent('WisdomX Orbis — Demo request')}` +
  `&body=${encodeURIComponent(
    [
      'Hello WisdomX team,',
      '',
      "I'd like to see a demo of the Orbis platform.",
      '',
      'Name:',
      'Company:',
      'Role:',
      'Estimated number of legacy systems:',
      '',
      'Thanks,',
    ].join('\n'),
  )}`;

/**
 * In-page anchor ids. The nav and the hero's secondary CTA scroll to these
 * instead of linking to pages that do not exist yet.
 */
export const SECTION_IDS = {
  platform: 'platform',
  // The nine report sections, in Figma order (node 2:90).
  executiveSummary: 'executive-summary',
  risk: 'risk-compliance',
  costSavings: 'cost-savings',
  legacyDecommission: 'legacy-decommissioning',
  vulnerability: 'vulnerability-management',
  threatIntel: 'threat-intelligence',
  aiSoc: 'ai-security-operations',
  techPortfolio: 'technology-portfolio',
  governance: 'governance-compliance',
  consultation: 'consultation',
  demo: 'demo',
  marketplace: 'azure-marketplace',
} as const;

/** Azure Marketplace listing. Opens in a new tab. */
export const AZURE_MARKETPLACE_URL =
  'https://marketplace.microsoft.com/en-us/product/saas/wisdomx33.wisdomx-orbis-decommissioning-intelligence';

/**
 * Product documentation site. Opens in a new tab.
 *
 * This link was made live on the VM directly rather than through the repo, so a
 * deploy built from main silently dropped it. Keeping the URL here means it
 * survives every rebuild.
 */
export const PRODUCT_DOCS_URL = 'https://learn.orbiswisdomx.com/';

/**
 * Static legal pages. The landing build has no router — `App.tsx` renders these
 * by matching `window.location.pathname`, and nginx's `try_files … /index.html`
 * hands every path to the same document.
 */
export const LEGAL_PATHS = {
  privacy: '/privacy-policy',
  terms: '/terms',
} as const;

/**
 * The four Orbis products. Drives BOTH the nav "Solutions" dropdown and the
 * footer "SOLUTIONS" column, so the two can never drift apart.
 *
 * Rendered as PLAIN TEXT, not links, on client instruction (2026-07-27): the
 * product pages do not exist yet, so there is nothing to point at and a dead
 * anchor would look broken. Give each an `href` here once they do.
 *
 * This replaced a nav dropdown that listed the nine report sections as in-page
 * anchors. The client asked for the product names instead.
 */
export const ORBIS_PRODUCTS = [
  'Orbis Decommission',
  'Orbis vulnerability management',
  'Orbis Threat Intel',
  'Orbis Security Operations',
] as const;

/** Footer "COMPANY" column — Figma nodes 2:524-2:526. */
export const COMPANY_DETAILS = {
  legalName: 'WISDOMX - BRAIN ON DEMAND LTD',
  companyNumber: 'Company number 1670864',
  officeLabel: 'Registered office address',
  office: '3rd Floor, 86-90 Paul Street, London, England, United Kingdom, EC2A 4NE',
  incorporated: 'Incorporated on 10 September 2025',
} as const;
